import { useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

interface AdminGuardProps {
  children: React.ReactNode;
  requiredRole?: 'agency_admin' | 'super_admin';
}

// Routes that are exclusively for super admins (not available during impersonation)
const SUPER_ADMIN_ONLY_ROUTES = [
  '/admin/agencies',
  '/admin/audit-logs',
];

export const AdminGuard = ({ children, requiredRole }: AdminGuardProps) => {
  const { user, loading } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Check for impersonation mode
  const isImpersonating = searchParams.get('impersonate') === 'agency';
  const impersonationSearch = location.search || '';

  useEffect(() => {
    if (!loading) {
      if (!user) {
        // Redirect to admin login, preserving the intended destination (including query params)
        const intended = `${location.pathname}${location.search || ''}`;
        navigate(`/login?redirect=${encodeURIComponent(intended)}`);
        return;
      }

      // Block super-admin-only routes when impersonating
      if (isImpersonating && SUPER_ADMIN_ONLY_ROUTES.some(route => location.pathname.startsWith(route))) {
        navigate(`/admin/dashboard${impersonationSearch}`, { replace: true });
        return;
      }

      if (requiredRole && user.role !== requiredRole && user.role !== 'super_admin') {
        // Super admin can access everything, but agency admin needs specific role
        navigate(isImpersonating ? `/admin/dashboard${impersonationSearch}` : '/admin/dashboard');
        return;
      }
    }
  }, [user, loading, navigate, location.pathname, location.search, requiredRole, isImpersonating, impersonationSearch]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect to login
  }

  // Block rendering for super-admin-only routes when impersonating
  if (isImpersonating && SUPER_ADMIN_ONLY_ROUTES.some(route => location.pathname.startsWith(route))) {
    return null; // Will redirect to dashboard
  }

  if (requiredRole && user.role !== requiredRole && user.role !== 'super_admin') {
    return null; // Will redirect to dashboard
  }

  return <>{children}</>;
};